<template>
  <footer class="footer">
    <span class="manga-sun footer__sun" aria-hidden="true"></span>
    <div class="container">
      <div class="footer-grid">
        <div class="footer-col footer-col--brand">
          <router-link to="/" class="footer-brand" aria-label="Mirai Motion — home">
            <img :src="logoWordmark" alt="Mirai Motion" class="footer-brand__logo" />
          </router-link>
          <p class="footer-desc">{{ $t('footer.description') }}</p>
          <a :href="ticketUrl" target="_blank" rel="noopener noreferrer" class="btn btn-neon footer-cta">
            {{ $t('nav.tickets') }} <span aria-hidden="true">→</span>
          </a>
        </div>

        <div class="footer-col">
          <h3 class="footer-title">{{ $t('footer.navigation') }}</h3>
          <ul class="footer-links">
            <li><router-link to="/">{{ $t('nav.home') }}</router-link></li>
            <li><router-link to="/events">{{ $t('nav.events') }}</router-link></li>
            <li><router-link to="/contact">{{ $t('nav.contact') }}</router-link></li>
          </ul>
        </div>

        <div class="footer-col">
          <h3 class="footer-title">{{ $t('footer.help') }}</h3>
          <ul class="footer-links">
            <li><router-link to="/terms">{{ $t('footer.terms') }}</router-link></li>
            <li><router-link to="/privacy">{{ $t('footer.privacy') }}</router-link></li>
          </ul>
        </div>

        <div class="footer-col">
          <h3 class="footer-title">{{ $t('footer.socials') }}</h3>
          <div v-if="socials.length" class="channel-row">
            <a
              v-for="s in socials"
              :key="s.key"
              :href="s.url"
              target="_blank"
              rel="noopener noreferrer"
              class="channel-link"
              :style="{ '--accent': s.bg, '--accent-ink': s.fg }"
              :aria-label="s.label"
            >
              <i class="fab" :class="s.icon"></i>
            </a>
          </div>
          <p v-else class="channel-soon">@mirai.motion</p>
        </div>
      </div>

      <div class="footer-bottom">
        <p>{{ $t('footer.copyright') }}</p>
      </div>
    </div>
  </footer>

  <button
    v-show="showScrollButton"
    @click="scrollToTop"
    class="to-top"
    aria-label="Naar boven"
  >
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="18 15 12 9 6 15"></polyline>
    </svg>
  </button>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { SOCIAL_LINKS } from '@/config/social.config.js';
import { TICKET_URL } from '@/config/site.config.js';
import logoWordmark from '@/assets/logo2.png';

const ticketUrl = TICKET_URL;

// Ordered platforms with their Font Awesome brand icon + accessible label.
// bg en fg zijn de stickerkleuren uit het merkpalet. Die gaan als inline
// variabele mee en niet als klassenaam: een klasse met "facebook" of
// "twitter" erin loopt hetzelfde risico op blockers als "social-row".
const SOCIAL_META = [
  { key: 'instagram', icon: 'fa-instagram', label: 'Instagram', bg: '#EF476F', fg: '#fff' },
  { key: 'tiktok', icon: 'fa-tiktok', label: 'TikTok', bg: '#2d1b45', fg: '#fff' },
  { key: 'snapchat', icon: 'fa-snapchat', label: 'Snapchat', bg: '#FFDD00', fg: '#2d1b45' },
  { key: 'discord', icon: 'fa-discord', label: 'Discord', bg: '#7a1fa2', fg: '#fff' },
  { key: 'facebook', icon: 'fa-facebook-f', label: 'Facebook', bg: '#4B0082', fg: '#fff' },
  { key: 'twitter', icon: 'fa-twitter', label: 'Twitter', bg: '#2d1b45', fg: '#fff' },
  { key: 'youtube', icon: 'fa-youtube', label: 'YouTube', bg: '#FF8A3D', fg: '#2d1b45' },
];
const socials = computed(() =>
  SOCIAL_META
    .filter((s) => SOCIAL_LINKS[s.key])
    .map((s) => ({ ...s, url: SOCIAL_LINKS[s.key] }))
);

const showScrollButton = ref(false);
const handleScroll = () => { showScrollButton.value = window.scrollY > 400; };
const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

onMounted(() => window.addEventListener('scroll', handleScroll, { passive: true }));
onUnmounted(() => window.removeEventListener('scroll', handleScroll));
</script>

<style scoped>
/* De footer sluit af zoals de hero opent: een zon met speed lines, hier
   rechtsboven en zachter, zodat hij achtergrond blijft. De lijnen staan
   stil. */
.footer {
  --sun-size: 260px;
  --sun-right: -70px;
  --sun-top: -120px;
  --sun-at: calc(100% - var(--sun-size) / 2 - var(--sun-right)) calc(var(--sun-size) / 2 + var(--sun-top));
  position: relative;
  overflow: hidden;
  border-top: 3px solid var(--ink);
  background: var(--void-2);
  padding: clamp(3rem, 6vw, 5rem) 1.5rem 2rem;
}
/* De lijnen doven uit vanaf de zon, zodat ze niet achter de links en de
   lopende tekst doorlopen. Dat werd op een smal scherm, waar alles onder
   elkaar staat, te onrustig. */
.footer::before {
  content: "";
  position: absolute;
  inset: 0;
  background: repeating-conic-gradient(
    from 0deg at var(--sun-at),
    color-mix(in srgb, var(--ink) 7%, transparent) 0deg 1.2deg,
    transparent 1.2deg 4deg
  );
  -webkit-mask-image: radial-gradient(circle at var(--sun-at), #000 0, #000 12%, transparent 60%);
  mask-image: radial-gradient(circle at var(--sun-at), #000 0, #000 12%, transparent 60%);
  pointer-events: none;
}
.footer__sun {
  top: var(--sun-top);
  right: var(--sun-right);
  width: var(--sun-size);
  opacity: 0.9;
}
.footer > .container { position: relative; }
.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 1fr;
  gap: 2.5rem;
}
.footer-col--brand { max-width: 340px; }

.footer-brand {
  display: inline-flex;
  align-items: center;
  margin-bottom: 1.2rem;
}
.footer-brand:hover { text-decoration: none; }
.footer-brand__logo { height: 46px; width: auto; display: block; }

.footer-desc { color: var(--muted); font-size: 0.92rem; margin-bottom: 1.5rem; }
.footer-cta { padding: 0.7rem 1.3rem; min-height: 44px; font-size: 0.8rem; }

.footer-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-display);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--cyan);
  margin-bottom: 1.2rem;
}
.footer-title::before {
  content: "";
  width: 0.65em;
  height: 0.65em;
  border-radius: 50%;
  background: var(--color-secondary);
  border: 2px solid var(--ink);
}
.footer-links { list-style: none; }
.footer-links li { margin-bottom: 0.35rem; }
/* Tapdoel van minstens 44px hoog, ook al oogt de link klein. */
.footer-links a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--muted);
  font-size: 0.92rem;
  transition: color 0.2s ease-out, translate 0.2s ease-out;
}
.footer-links a:hover { color: var(--text); translate: 4px 0; }

/* Klassenaam bewust NIET "social-row" of iets met "social" erin. Blockers
   verbergen zulke klassen standaard, want daar zit meestal een deelbalk van
   derden achter. Hier zijn het Mirai Motions eigen kanalen, dus dat was een
   vals alarm: op 2026-09-24 gemeten dat een LEGE div met class="social-row"
   in een browser met blocker meteen display:none kreeg, terwijl dezelfde div
   met een andere klassenaam gewoon zichtbaar bleef. Hernoem dit dus niet
   terug. */
/* De kanalen als ronde stickers, elk in een eigen merkkleur en om en om
   een beetje scheef. 46px, dus boven het minimale tapdoel van 44px. */
.channel-row { display: flex; flex-wrap: wrap; gap: 0.8rem; }
.channel-link {
  --accent: #EF476F;
  --accent-ink: #fff;
  display: grid;
  place-items: center;
  width: 46px; height: 46px;
  border-radius: 50%;
  font-size: 1.15rem;
  color: var(--accent-ink);
  background: var(--accent);
  border: 2.5px solid var(--ink);
  box-shadow: 3px 3px 0 var(--ink);
  rotate: -6deg;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease-out;
}
.channel-link:nth-child(even) { rotate: 6deg; }
.channel-link:hover,
.channel-link:focus-visible {
  color: var(--accent-ink);
  transform: translate(-2px, -2px) rotate(-10deg) scale(1.08);
  box-shadow: 5px 5px 0 var(--ink);
}
.channel-soon { color: var(--muted); font-size: 0.9rem; }

.footer-bottom {
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 2px dashed var(--line);
  text-align: center;
}
.footer-bottom p { color: var(--muted); font-size: 0.85rem; }

/* Naar-boven als ronde sticker, zelfde taal als de rest. */
.to-top {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  width: 48px; height: 48px;
  display: grid;
  place-items: center;
  border: 2.5px solid var(--ink);
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  background: #4B0082;
  box-shadow: 4px 4px 0 var(--ink);
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease-out;
}
.to-top:hover { transform: translate(-2px, -3px) scale(1.06); box-shadow: 6px 7px 0 var(--ink); }
.to-top:active { transform: translate(3px, 3px); box-shadow: 1px 1px 0 var(--ink); }

@media (prefers-reduced-motion: reduce) {
  .channel-link,
  .to-top,
  .footer-links a { transition: none; }
}

@media (max-width: 860px) {
  .footer { --sun-size: 180px; --sun-right: -60px; --sun-top: -90px; }
  .footer-grid { grid-template-columns: 1fr 1fr; gap: 2rem; }
  .footer-col--brand { grid-column: 1 / -1; max-width: none; }
}
@media (max-width: 640px) {
  .to-top { bottom: 16px; right: 16px; }
  /* Ruimte onder de copyrightregel, anders valt de naar-boven-knop erover. */
  .footer-bottom { padding-bottom: 3.5rem; }
}
@media (max-width: 480px) {
  .footer-grid { grid-template-columns: 1fr; }
}
</style>
